import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { AUTH_URL } from 'src/configs';
import request from 'src/request';
import { AdminSession } from 'src/types/admin';

interface AdminSessionState {
  admin: AdminSession | null;
  permissions: string[];
  loading: boolean;
  error: string | null;
  initialized: boolean;
}

const initialState: AdminSessionState = {
  admin: null,
  permissions: [],
  loading: false,
  error: null,
  initialized: false,
};

export const fetchAdminProfile = createAsyncThunk('adminSession/fetchAdminProfile', async (_, { rejectWithValue }) => {
  try {
    const response = await request.get(`${AUTH_URL}/v1/admin/me`);
    return response.data.admin as AdminSession;
  } catch (error: any) {
    const status = error?.response?.status;
    const message = error?.response?.data?.error ?? error?.message ?? 'Failed to fetch admin profile';

    if (status === 401 || status === 403) {
      localStorage.removeItem('access');
    }

    return rejectWithValue(message);
  }
});

const adminSessionSlice = createSlice({
  name: 'adminSession',
  initialState,
  reducers: {
    clearAdminSession: (state) => {
      state.admin = null;
      state.permissions = [];
      state.loading = false;
      state.error = null;
      state.initialized = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAdminProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAdminProfile.fulfilled, (state, action) => {
        state.admin = action.payload;
        state.permissions = action.payload.permissions ?? [];
        state.loading = false;
        state.error = null;
        state.initialized = true;
      })
      .addCase(fetchAdminProfile.rejected, (state, action) => {
        state.admin = null;
        state.permissions = [];
        state.loading = false;
        state.error = (action.payload as string) ?? action.error.message ?? 'Failed to fetch admin profile';
        state.initialized = true;
      });
  },
});

export const { clearAdminSession } = adminSessionSlice.actions;

export default adminSessionSlice.reducer;
